import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload } from '../components/icons';
import { useConfig } from '../context/ConfigContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/ReceiptDetail.jsx. `receipt.donor`
// -> `receipt.member` (see ReceiptController::show()'s `member.user:...`
// eager load) and the inventory item link moves to `/resources/inventory/:id`.
const METHOD_LABELS: Record<string, string> = {
  paynow: 'PayNow',
  bank_transfer: 'Bank Transfer',
  cash: 'Cash',
  other: 'Other',
};

interface ReceiptRecord {
  id: number;
  receipt_number: string;
  paid_amount: number;
  payment_method: string;
  payment_date: string;
  type: string | null;
  category: string | null;
  description: string | null;
  customer_invoice: { id: number; invoice_number: string; customer_name: string } | null;
  payer_name: string;
  member: { user: { first_name: string; last_name: string } | null } | null;
  received_by: { full_name: string } | null;
  inventory_movement: {
    quantity_change: number;
    inventory_item: { id: number; name: string } | null;
  } | null;
}

export function ReceiptDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [receipt, setReceipt] = useState<ReceiptRecord | null>(null);

  useEffect(() => {
    api.get<ReceiptRecord>(`/receipts/${id}`).then((res) => setReceipt(res.data));
  }, [id]);

  const download = () => receipt && downloadFile(api, `/receipts/${id}/pdf`, `${receipt.receipt_number}.pdf`);

  if (!receipt) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/revenue/receipts')} />
          <IconButton icon={IconDownload} label="Download PDF" onClick={download} />
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{receipt.receipt_number}</div>
            <div className="detail-summary-amount">{fmtMoney(receipt.paid_amount)}</div>
            <div className="detail-summary-service">
              {receipt.customer_invoice?.customer_name ?? receipt.payer_name}
            </div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Sale type</span>
            <span className="detail-item-value">{receipt.customer_invoice ? 'Credit Sale' : 'Cash Sale'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Invoice</span>
            {receipt.customer_invoice ? (
              <Link className="detail-item-value" to={`/revenue/invoices/${receipt.customer_invoice.id}`}>
                {receipt.customer_invoice.invoice_number}
              </Link>
            ) : (
              <span className="detail-item-value empty">No invoice</span>
            )}
          </div>
          <div className="detail-item">
            <span className="detail-item-label">{receipt.customer_invoice ? 'Customer name' : 'Payer name'}</span>
            <span className="detail-item-value">{receipt.customer_invoice?.customer_name ?? receipt.payer_name}</span>
          </div>
          {!receipt.customer_invoice && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Type</span>
                <span className="detail-item-value">
                  {receipt.type ? receipt.type.replace(/_/g, ' ') : <span className="empty">—</span>}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Category</span>
                <span className="detail-item-value">
                  {receipt.category ? receipt.category.replace(/_/g, ' ') : <span className="empty">—</span>}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Member</span>
                <span className="detail-item-value">
                  {receipt.member?.user ? (
                    `${receipt.member.user.first_name} ${receipt.member.user.last_name}`
                  ) : (
                    <span className="empty">Not a member</span>
                  )}
                </span>
              </div>
              {receipt.type === 'inventory' && receipt.inventory_movement?.inventory_item && (
                <div className="detail-item">
                  <span className="detail-item-label">Item sold</span>
                  <span className="detail-item-value">
                    <Link to={`/resources/inventory/${receipt.inventory_movement.inventory_item.id}`}>
                      {receipt.inventory_movement.inventory_item.name}
                    </Link>
                    {' × '}
                    {Number(receipt.inventory_movement.quantity_change) * -1}
                  </span>
                </div>
              )}
            </>
          )}
          <div className="detail-item">
            <span className="detail-item-label">Amount ({settings.currency})</span>
            <span className="detail-item-value">{fmtMoney(receipt.paid_amount)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Payment method</span>
            <span className="detail-item-value">{METHOD_LABELS[receipt.payment_method] ?? receipt.payment_method}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Payment date</span>
            <span className="detail-item-value">{receipt.payment_date}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Received by</span>
            <span className="detail-item-value">{receipt.received_by?.full_name}</span>
          </div>
          {!receipt.customer_invoice && receipt.description && (
            <div className="detail-item detail-item-full">
              <span className="detail-item-label">Description</span>
              <div className="detail-notes">{receipt.description}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
